import {PointerEvent, useEffect, useState} from "react";
import {IoPause, IoPlay} from "react-icons/io5";
import {MdForward10, MdReplay10} from "react-icons/md";

const POLL_INTERVAL_MS = 250;
const SKIP_SECONDS = 10;

type AudioPlayer = {
    play: () => Promise<void> | void;
    pause: () => void;
    seek: (time: number) => boolean;
    getCurrentTime: () => number;
    getDuration: () => number;
    isPlaying: () => boolean;
};

const finiteOrZero = (value: unknown) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
};

export const formatTime = (seconds: number) => {
    const total = Math.floor(finiteOrZero(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = String(total % 60).padStart(2, "0");
    return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${secs}` : `${minutes}:${secs}`;
};

/**
 * Controls for podcasts. A podcast stream has no picture, so the native video controls show
 * a blank player and disappear after a few seconds. These stay on screen and are driven
 * through the player API, so they behave the same in the iOS and Android WebViews.
 */
const PodcastControls = ({player}: {player: AudioPlayer}) => {
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [dragPosition, setDragPosition] = useState<number | null>(null);

    useEffect(() => {
        const sync = () => {
            setPosition(finiteOrZero(player.getCurrentTime()));
            setDuration(finiteOrZero(player.getDuration()));
            setIsPlaying(!!player.isPlaying());
        };
        sync();
        const interval = setInterval(sync, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [player]);

    const seekTo = (time: number) => {
        const target = Math.min(Math.max(time, 0), duration || time);
        player.seek(target);
        setPosition(target);
    };

    const togglePlay = () => {
        if (isPlaying) {
            player.pause();
            return;
        }
        Promise.resolve(player.play()).catch((error) => console.warn("Playback did not resume:", error));
    };

    const positionAt = (event: PointerEvent<HTMLDivElement>) => {
        const {left, width} = event.currentTarget.getBoundingClientRect();
        const ratio = width ? (event.clientX - left) / width : 0;
        return Math.min(Math.max(ratio, 0), 1) * duration;
    };

    const startDrag = (event: PointerEvent<HTMLDivElement>) => {
        if (!duration) return;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDragPosition(positionAt(event));
    };

    const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
        if (dragPosition !== null) setDragPosition(positionAt(event));
    };

    const endDrag = (event: PointerEvent<HTMLDivElement>) => {
        if (dragPosition === null) return;
        seekTo(positionAt(event));
        setDragPosition(null);
    };

    const shownPosition = dragPosition ?? position;
    const progress = duration ? Math.min((shownPosition / duration) * 100, 100) : 0;

    return (
        <div className="absolute inset-x-0 bottom-0 z-20 bg-black bg-opacity-70 px-4 pb-1 text-white">
            <div
                role="slider"
                aria-label="Seek"
                aria-valuemin={0}
                aria-valuemax={Math.floor(duration)}
                aria-valuenow={Math.floor(shownPosition)}
                className="py-3 cursor-pointer"
                style={{touchAction: "none"}}
                onPointerDown={startDrag}
                onPointerMove={moveDrag}
                onPointerUp={endDrag}
                onPointerCancel={() => setDragPosition(null)}
            >
                <div className="relative h-1 rounded-full bg-white bg-opacity-40">
                    <div className="absolute inset-y-0 left-0 rounded-full bg-white" style={{width: `${progress}%`}}/>
                    <div
                        className="absolute top-1/2 w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
                        style={{left: `${progress}%`}}
                    />
                </div>
            </div>
            <div className="flex items-center justify-between">
                <span className="w-12 text-xs tabular-nums">{formatTime(shownPosition)}</span>
                <div className="flex items-center gap-5">
                    <button aria-label="Back 10 seconds" className="p-1" onClick={() => seekTo(position - SKIP_SECONDS)}>
                        <MdReplay10 size={26}/>
                    </button>
                    <button
                        aria-label={isPlaying ? "Pause" : "Play"}
                        className="w-10 h-10 bg-white text-black rounded-full flex items-center justify-center"
                        onClick={togglePlay}
                    >
                        {isPlaying ? <IoPause size={20}/> : <IoPlay size={20}/>}
                    </button>
                    <button aria-label="Forward 10 seconds" className="p-1" onClick={() => seekTo(position + SKIP_SECONDS)}>
                        <MdForward10 size={26}/>
                    </button>
                </div>
                <span className="w-12 text-xs tabular-nums text-right">{formatTime(duration)}</span>
            </div>
        </div>
    );
};

export default PodcastControls;
